import React from 'react';
import { useRecoilValue } from 'recoil';
import { isMobileState, modalState } from '../../recoil/selectors';
import { ViewProduct } from '../../models/ViewProduct.model';
import { ModalToggleState } from '../../models/actionStates';
import { QuickView } from './QuickView';
import { Color } from '../../enums/Color.enum';
import { SubContext } from '../../enums/SubContext.enum';
import { useAdvanceModalState, useSetModalState } from '../../hooks/actions/useModalState';
import { ModalState } from '../../models/ModalState';
import { QuickViewMobile } from './QuickViewMobile';
import { SelectionType } from '../../enums/SelectionType.enum';
import { useProductSelection } from '../../hooks/useProductSelection';
import { trackProductDetailViewed } from '../../tracking/events';
import { shopifyState } from '../../recoil/atoms';
import { Button, ButtonBaseProps, ButtonTrackingProps } from '../../root/components/buttons/Button';
import { ImageButton } from '../../root/components/buttons/ImageButton';

interface QuickViewLinkProps extends ButtonBaseProps, ButtonTrackingProps {
  product: ViewProduct;
  subContext: SubContext;
  selectionKey: string;
  initialColor?: Color;
  image?: React.ReactElement<HTMLImageElement>;
}

export const QuickViewLink: React.FC<QuickViewLinkProps> = ({
  product,
  initialColor,
  subContext,
  dataTrackLabel,
  dataTrackLocation,
  selectionKey,
  image,
  ...buttonBaseProps
}) => {
  const modal = useRecoilValue(modalState);
  const advanceModal = useAdvanceModalState();
  const setModal = useSetModalState();
  const color = initialColor ?? product?.colors?.[0];
  const { setSubContext } = useProductSelection(SelectionType.QuickView, selectionKey, product.handle, color);
  const isMobile = useRecoilValue(isMobileState);
  const modalContents = isMobile ? <QuickViewMobile selectionKey={selectionKey} /> : <QuickView selectionKey={selectionKey} />;
  const shopify = useRecoilValue(shopifyState);
  const initialize = () => {
    setSubContext(subContext);
    trackProductDetailViewed(shopify, product, undefined, color, undefined);
  };

  if (image) {
    return (
      <ImageButton
        dataTrackLabel={dataTrackLabel}
        dataTrackLocation={dataTrackLocation}
        onClick={openQuickView(setModal, advanceModal, initialize, modal, modalContents, isMobile)}
        image={image}
      />
    );
  } else {
    return (
      <Button
        dataTrackLabel={dataTrackLabel}
        dataTrackLocation={dataTrackLocation}
        onClick={openQuickView(setModal, advanceModal, initialize, modal, modalContents, isMobile)}
        {...buttonBaseProps}
      />
    );
  }
};

const openQuickView =
  (
    setModal: (state: ModalState) => void,
    advanceModal: (state: ModalState) => void,
    initialize: () => void,
    modal: ModalState,
    modalContents: JSX.Element,
    isMobile: boolean,
  ) =>
  () => {
    initialize();
    const modalHeightWidth = isMobile ? '100%' : 'auto';

    const existingModal = modal?.toggleState === ModalToggleState.Open;
    const modalAction = existingModal ? advanceModal : setModal;

    modalAction({
      contents: modalContents,
      height: modalHeightWidth,
      width: modalHeightWidth,
      maxWidth: '100vw',
      maxHeight: 'unset',
      toggleState: ModalToggleState.Open,
      scrollToTop: false,
    });
  };

export default QuickViewLink;
